import * as React from 'react';
import { Icon } from 'office-ui-fabric-react/lib';
import { ControlIcons } from 'owa-control-icons';
import { IconButton } from 'office-ui-fabric-react/lib/Button';
import { observer } from 'mobx-react';
import {
    CompactOpeningHours,
    WeekDays,
    getLocalizedShortDayOfWeek,
    getTodaysOpeningHours,
    isOpenNow,
    getToday,
    getFormattedTimeRangeString,
    getNextOpenTimes,
} from '../utils/openingHoursUtils';
import { OwaDateRange } from 'owa-datetime-utils/lib/OwaDateRange';
import { OwaDate, formatUserTime } from 'owa-datetime';
import strings from 'owa-strings/owa-calendar-strings';
import { format } from 'owa-strings';
import { logUsage } from 'owa-analytics';
import datapoints from '../datapoints';

let styles = require('./OpeningHours.scss');
let classNames = require('classnames/bind').bind(styles);

export interface OpeningHoursProps {
    openingHours: CompactOpeningHours;
    currentTime: OwaDate;
}

export interface OpeningHoursState {
    isCollapsed: boolean;
}

/**
 * This is the list of opening hours in the detail section of the location card.
 */
@observer
export default class OpeningHours extends React.Component<OpeningHoursProps, OpeningHoursState> {
    constructor(props: OpeningHoursProps) {
        super(props);

        this.state = {
            isCollapsed: true,
        };
    }

    render() {
        return (
            <div className={styles.openingHoursContainer}>
                <Icon className={styles.timeIcon} iconName={ControlIcons.Clock} />
                <div className={styles.openingHoursDetails}>{this.renderOpeningHours()}</div>

                <IconButton
                    className={styles.chevronIcon}
                    iconProps={{
                        iconName: this.state.isCollapsed
                            ? ControlIcons.ChevronDown
                            : ControlIcons.ChevronUp,
                        styles: {
                            root: {
                                fontSize: '12px',
                            },
                        },
                    }}
                    onClick={this.toggleOpeningHours}
                />
            </div>
        );
    }

    private renderOpeningHours = (): JSX.Element => {
        const { openingHours, currentTime } = this.props;

        let openingHoursSummary = '';
        if (isOpenNow(openingHours, currentTime)) {
            // if the times are collapsed show "Open now: 9:30 AM - 12:30 PM, 1:45 PM - 5:45 PM"
            // otherwise show "Open now"
            openingHoursSummary = this.state.isCollapsed
                ? format(
                      strings.detailedOpenNow,
                      getTodaysOpeningHours(openingHours, currentTime)
                          .map(time => getFormattedTimeRangeString(time))
                          .join(', ')
                  )
                : strings.openNow;
        } else {
            let nextOpenTimes = getNextOpenTimes(openingHours, getToday(currentTime));
            // if the location is closed than show the next time it is opened again
            openingHoursSummary = this.state.isCollapsed
                ? format(strings.detailedClosedNow, formatUserTime(nextOpenTimes.times[0].start))
                : strings.closedNow;
        }

        return (
            <>
                <div className={styles.openingHoursHeader} onClick={this.toggleOpeningHours}>
                    {openingHoursSummary}
                </div>
                {!this.state.isCollapsed &&
                    Object.keys(openingHours).map(day =>
                        this.renderOpeningHoursRow(day, openingHours[day])
                    )}
            </>
        );
    };

    // renders one row of working hours.
    private renderOpeningHoursRow = (day: string, openingTimes: OwaDateRange[]): JSX.Element => {
        const isOpenToday =
            day == getToday(this.props.currentTime) &&
            isOpenNow(this.props.openingHours, this.props.currentTime);

        return (
            <div key={day} className={styles.openingHoursRow}>
                <div
                    className={classNames(styles.days, {
                        isOpenToday: isOpenToday,
                    })}>
                    {getLocalizedShortDayOfWeek(day as WeekDays)}
                </div>
                <div
                    className={classNames(styles.times, {
                        isOpenToday: isOpenToday,
                    })}>
                    {openingTimes.map((openingTime, index) => (
                        <div key={day + index} className={styles.timeSlot}>
                            {getFormattedTimeRangeString(openingTime)}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    /** toggle the collapsed state */
    private toggleOpeningHours = () => {
        const customData = datapoints.toggleOpeningHours.datapoint.customData(
            this.state.isCollapsed ? 'open' : 'collapse'
        );
        logUsage(datapoints.toggleOpeningHours.datapoint.name, customData);
        this.setState({ isCollapsed: !this.state.isCollapsed });
    };
}
